import {
  useGetCampaignScheduledEmails,
  useGetCampaignSequence,
} from '../sequences_api'
import { ScheduledEmailsTable } from './ScheduledEmailsTable'

/**
 * ==================== SCHEDULED EMAILS TAB ====================
 * NOTE: This displays data from a PASSTHROUGH API - data is fetched directly
 * from EmailBison API on each request, NOT from local database.
 *
 * IMPORTANT: Unlike other data (leads, sender emails) which are synced to our
 * database via webhooks, scheduled emails are always fetched fresh because:
 * - Scheduled email status changes frequently (scheduled -> sent -> replied)
 * - No webhook events trigger sync for scheduled emails
 * - Local data would be stale immediately after any email is sent
 *
 * This is intentionally different from synced data patterns.
 */

interface ScheduledEmailsTabProps {
  campaignId: number
}

export function ScheduledEmailsTab({ campaignId }: ScheduledEmailsTabProps) {
  // Get sequence to access bison_campaign_id
  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId)

  const bisonCampaignId = sequence?.bison_campaign_id

  const {
    data: scheduledEmailsData,
    isLoading: isLoadingScheduledEmails,
    isFetching,
  } = useGetCampaignScheduledEmails(bisonCampaignId || 0)

  if (isLoadingSequence || (isLoadingScheduledEmails && !scheduledEmailsData)) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!bisonCampaignId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">No EmailBison campaign found</div>
      </div>
    )
  }

  if (scheduledEmailsData && scheduledEmailsData.count === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="text-gray-400 mb-2">
          <svg
            className="w-16 h-16 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          No Scheduled Emails
        </h3>
        <p className="text-sm text-gray-500 max-w-sm">
          Scheduled emails will appear here once your campaign starts sending.
        </p>
      </div>
    )
  }

  return (
    <ScheduledEmailsTable data={scheduledEmailsData} isLoading={isFetching} />
  )
}
